import { type Aperture, type ComponentEffect, toRender } from "refract-rxjs";
import { type Observable, combineLatest } from "rxjs";
import { filter, map, startWith, switchMap } from "rxjs/operators";

import { type MetricField } from "../../../gql-generated/types";
import { type GetOrganicStatsQuery } from "../../api/queries";
import {
    type AgeGenderMetrics,
    type CountriesStatsItem,
    type InsightsMetrics,
    type OsStatsItem,
    createDefaultInsightsMetrics,
} from "../../models";

import { mapAudienceStats } from "./mapAudienceStats";

export interface AudienceStatsInput {
    dimensionBreakdownData: GetOrganicStatsQuery | undefined;
    dimensionBreakdownLoading: boolean;
    baseMetricField: MetricField;
}

export interface AudienceStatsOutput {
    countryMetrics: InsightsMetrics<CountriesStatsItem>;
    interestMetrics: InsightsMetrics<CountriesStatsItem>;
    ageGenderMetrics: AgeGenderMetrics;
    osMetrics: InsightsMetrics<OsStatsItem>;
}

export const defaults: AudienceStatsOutput = {
    ageGenderMetrics: { stats: [], menPct: "-", womenPct: "-", isEmpty: true },
    countryMetrics: createDefaultInsightsMetrics<CountriesStatsItem>(),
    interestMetrics: createDefaultInsightsMetrics<CountriesStatsItem>(),
    osMetrics: createDefaultInsightsMetrics<OsStatsItem>(),
};

export const renderAudienceStatsEffect = (
    data$: Observable<GetOrganicStatsQuery | undefined>,
    loading$: Observable<boolean>,
    baseMetricField$: Observable<MetricField>,
): Observable<ComponentEffect<AudienceStatsOutput>> => {
    return combineLatest([data$, baseMetricField$, loading$]).pipe(
        filter((values): values is [GetOrganicStatsQuery, MetricField, boolean] => {
            const [data, _, loading] = values;
            return !loading && data !== undefined && !!data.getMyLensesOrganicStats;
        }),
        switchMap(([data, baseMetricField]) => mapAudienceStats(data, baseMetricField)),
        startWith(defaults),
        map((metrics) => toRender<AudienceStatsOutput>(metrics)),
    );
};

export const audienceStatsEffect: Aperture<AudienceStatsInput, ComponentEffect<AudienceStatsOutput>> = ({
    observe,
}) => {
    const dimensionBreakdownData$ = observe<GetOrganicStatsQuery>("dimensionBreakdownData");
    const dimensionBreakdownLoading$ = observe<boolean>("dimensionBreakdownLoading");
    const baseMetricField$ = observe<MetricField>("baseMetricField");

    return renderAudienceStatsEffect(dimensionBreakdownData$, dimensionBreakdownLoading$, baseMetricField$);
};
